import {ComponentRef, Injectable, Type} from '@angular/core';
import {InjectionService} from '../../injections/injection/injection.service';
import {Observable, of} from 'rxjs';
import {delay, switchMap} from 'rxjs/operators';
import {AbstractModal} from './abstract-modal';

@Injectable()
export class ModalService<T extends AbstractModal> {
    public modalComponent: ComponentRef<T>;

    constructor(private injectionService: InjectionService) {
    }

    /**
     * Create and configure the modal component.
     * @param type
     * @param properties
     */
    public create(type: Type<T>, properties: object): Observable<ComponentRef<T>> {
        return this.injectionService.post<T>(type).pipe(switchMap((componentRef) => {
            this.modalComponent = componentRef;
            this.modalComponent.instance.loaded = false;

            Object.keys(properties).forEach((key) => {
                this.modalComponent.instance[key] = properties[key];
            });

            return of(this.modalComponent).pipe(delay(250));
        }));
    }

    /**
     * Remove the modal from the DOM.
     */
    public destroy(): void {
        this.injectionService.delete<T>(this.modalComponent);
    }

    /**
     * Display the created modal, subscribe to standard event emissions and
     * triage the actions accordingly.
     */
    public load(): Observable<ComponentRef<T>> {
        this.modalComponent.instance.loaded = true;

        return of(this.modalComponent).pipe(delay(250));
    }

    /**
     * Hide the instance but don't yet remove it from the DOM. This helps
     * with keeping the modal in memory for later use or fading out elegantly.
     */
    public unload(): Observable<ComponentRef<T>> {
        this.modalComponent.instance.loaded = false;

        return of(this.modalComponent).pipe(delay(250));
    }
}
